import { memo, useCallback, useState, type ReactNode } from 'react'
import { fbt } from 'fbtee'
import { ChevronDown, ChevronLeft, ChevronRight, Heart } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { parseDateOnly, toBcp47 } from '@shared/dates'
import { addDays, shiftMonth, startOfWeek, type DayMark, type WeekStartsOn } from '@/lib/eventCalendar'
import { track } from '@/lib/analytics'
import { cn } from '@/lib/utils'
import { useHorizontalDrag } from '@/hooks/useHorizontalDrag'
import { useLatestRef } from '@/hooks/useLatestRef'
import { useHistoryState } from '@/hooks/useHistoryState'
import { useVisitState } from '@/hooks/useTabVisit'
import { useDismissOnScroll } from '@/hooks/useDismissOnScroll'
import { GoingCheckIcon } from '@/components/GoingCheckIcon'
import { PlansCalendarMonth } from './PlansCalendarMonth'
import { PlansCalendarTitle } from './PlansCalendarTitle'
import { WEEK_ROW_REM, weekWindow } from './calendarWeeks'

const EXPANDED_KEY = 'beacon_home_calendar_expanded'

type CalendarPlace = { month: string; openDay: string | null }

const MonthPage = memo(PlansCalendarMonth)

function readExpanded(): boolean {
  try {
    return sessionStorage.getItem(EXPANDED_KEY) === '1'
  } catch {
    return false
  }
}

function useWidth() {
  const [width, setWidth] = useState(0)
  const measure = useCallback((el: HTMLElement | null) => {
    if (!el) return
    const observer = new ResizeObserver(() => setWidth(el.offsetWidth))
    observer.observe(el)
    return () => observer.disconnect()
  }, [])
  return [width, measure] as const
}

/**
 * What the viewport shows, in week rows: how many (its height) and how far the
 * current page is shifted up (`top`). At rest it's the current page's window;
 * mid-drag both blend toward the incoming month's by how far it's in, so a
 * collapsed page opens its other weeks in step; while snapping, the destination's.
 */
function viewportFrame({ month, today, weekStartsOn, expanded, settle, collapsing, offset, width }: {
  month: string
  today: string
  weekStartsOn: WeekStartsOn
  expanded: boolean
  settle: number | null
  collapsing: boolean
  offset: number
  width: number
}) {
  const rest = weekWindow(month, today, weekStartsOn, expanded)
  const monthRows = (delta: number) => weekWindow(shiftMonth(month, delta), today, weekStartsOn, true).rows
  if (settle === 0) return rest
  if (collapsing && settle !== null) return weekWindow(shiftMonth(month, settle), today, weekStartsOn, false)
  if (settle !== null) return { top: 0, rows: monthRows(settle) }
  if (offset === 0 || !width) return rest
  const p = Math.min(Math.abs(offset) / width, 1)
  return { top: rest.top * (1 - p), rows: rest.rows + (monthRows(offset < 0 ? 1 : -1) - rest.rows) * p }
}

function monthsApart(from: string, to: string) {
  const [fy, fm] = from.split('-').map(Number)
  const [ty, tm] = to.split('-').map(Number)
  return (ty - fy) * 12 + tm - fm
}

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/**
 * The viewer's plans on a calendar: two weeks from this one by default (a
 * month grid with one or two plans reads as empty), expandable to a month.
 * Dragging sideways pages through months, following the finger and snapping to
 * the nearest month on release. Neighbor months are always full months, so a
 * drag from the collapsed two weeks grows into the neighbor month as it slides in.
 * Earlier months page back too, to look over past plans.
 */
export function PlansCalendar({
  loading,
  marks,
  today,
  locale,
  weekStartsOn,
  crewHidden,
  settled = false,
  renderDayPlans,
}: {
  loading?: boolean
  marks?: Map<string, DayMark>
  /** The marks have finished loading; crew marks that arrive before then don't animate in. */
  settled?: boolean
  today: string
  locale: string
  weekStartsOn: WeekStartsOn
  /** The crew filter is off: the legend keeps its Crew item, grayed out. */
  crewHidden?: boolean
  /** A day's plans, or that it has none, shown in a popover when the day is tapped. */
  renderDayPlans?: (day: string) => ReactNode
}) {
  const [expanded, setExpanded] = useState(readExpanded)
  const currentMonth = `${today.slice(0, 7)}-01`
  // The month and the open day live in the history entry, so swiping back from
  // an event opened from a day's popover lands where the viewer left off.
  // Rendered from a local copy: the history write lands in a router transition, a
  // frame after the settle resets, which would flash the old month at rest. The
  // copy re-reads the entry each visit, so coming back through the nav bar (or
  // after Discover replaced the entry) starts closed.
  const [savedPlace, savePlace] = useHistoryState<CalendarPlace | null>('homeCalendar', null)
  const [place, setLocalPlace] = useVisitState(savedPlace)
  const setPlace = (next: CalendarPlace) => {
    setLocalPlace(next)
    savePlace(next)
  }
  const month = place?.month ?? currentMonth
  const openDay = place?.openDay ?? null
  const setMonth = (m: string) => setPlace({ month: m, openDay: null })
  const setOpenDay = (day: string | null) => setPlace({ month, openDay: day })
  /** How many months the track is sliding (back to 0, or ±1 while paging); null when at rest. */
  const [settle, setSettle] = useState<number | null>(null)
  /** Collapsing from another month: the track is sliding back through the months in between. */
  const [collapsing, setCollapsing] = useState(false)
  /** The snap underway was a drag's release, not a tap. */
  const [snapFromDrag, setSnapFromDrag] = useState(false)
  /** Which way the last drag headed: +1 toward the next month. */
  const [dragDir, setDragDir] = useState<1 | -1>(1)
  const [width, measureViewport] = useWidth()
  const renderDayPlansRef = useLatestRef(renderDayPlans)
  const dayPlans = useCallback((day: string) => renderDayPlansRef.current?.(day), [renderDayPlansRef])
  const setOpenDayRef = useLatestRef(setOpenDay)
  const dayToggled = useCallback((day: string, open: boolean, marked: boolean) => {
    if (open && marked) track('home_calendar_day_tapped', {})
    setOpenDayRef.current(open ? day : null)
  }, [setOpenDayRef])
  useDismissOnScroll(openDay !== null, () => setOpenDay(null), '[data-testid="plans-day-popover"]')

  function commit(delta: number) {
    setSettle(null)
    if (collapsing) {
      setCollapsing(false)
      setMonth(currentMonth)
      persistExpanded(false)
      return
    }
    if (delta === 0) return
    setMonth(shiftMonth(month, delta))
    if (!expanded) persistExpanded(true)
  }

  function settleTo(delta: -1 | 0 | 1, fromDrag = false) {
    setSnapFromDrag(fromDrag)
    if (prefersReducedMotion()) commit(delta)
    else setSettle(delta)
  }

  function toggle() {
    if (collapsing) return
    if (expanded && month !== currentMonth && !prefersReducedMotion()) {
      setCollapsing(true)
      setSnapFromDrag(false)
      setSettle(-monthsApart(currentMonth, month))
      return
    }
    if (expanded) setMonth(currentMonth)
    persistExpanded(!expanded)
  }

  function persistExpanded(next: boolean) {
    setExpanded(next)
    if (next) track('home_calendar_expanded', {})
    try {
      sessionStorage.setItem(EXPANDED_KEY, next ? '1' : '0')
    } catch {
      // Not remembered; the choice still applies for this visit.
    }
  }

  const { dragX, handlers } = useHorizontalDrag({
    onStart: () => {
      if (settle !== null) commit(settle)
    },
    onEnd: (dx, velocity) => {
      if (dx === 0) return
      const flung = Math.abs(velocity) > 0.4 && Math.abs(dx) > 20
      setDragDir(dx < 0 ? 1 : -1)
      settleTo(Math.abs(dx) > (width || 1) / 4 || flung ? (dx < 0 ? 1 : -1) : 0, true)
    },
  })

  if (loading || !marks || !renderDayPlans) return <Skeleton className="h-36 w-full rounded-xl" />

  const shown = expanded && !collapsing
  const bcp47 = toBcp47(locale)
  const formatMonth = (m: string) => parseDateOnly(m).toLocaleDateString(bcp47, { month: 'long', year: 'numeric' })
  const weekStart = startOfWeek(today, weekStartsOn)
  const offset = dragX ?? 0
  const frame = viewportFrame({ month, today, weekStartsOn, expanded, settle, collapsing, offset, width })
  // A drag follows the finger. A snap after one eases out, carrying its momentum; a tap
  // (expand, collapse, the arrows) starts from rest, so it eases in too or the first frame
  // lurches. A slide back across several months takes a little longer.
  const curve = settle !== null && snapFromDrag ? 'ease-out' : 'ease-in-out'
  const easing = dragX === null && cn('transition-[height,transform] duration-300 motion-reduce:transition-none', curve)
  const duration = settle && Math.abs(settle) > 1 ? `${Math.min(300 + 80 * Math.abs(settle), 700)}ms` : undefined
  // Every page shares the shift, so the incoming month's rows line up with the current one's.
  const shift = { transform: `translateY(${-frame.top * WEEK_ROW_REM}rem)`, transitionDuration: duration }
  // The pages the track holds, by offset from `month`: its neighbors, plus every month back to this one while collapsing.
  const first = Math.min(-1, collapsing ? settle ?? 0 : 0)
  const last = Math.max(1, collapsing ? settle ?? 0 : 0)
  const offsets = Array.from({ length: last - first + 1 }, (_, i) => first + i)
  // A drag, and the snap that ends it, carry the title and the arrows along by how far
  // the next month is in; a tap (the bar, the arrows) animates them on its own.
  const following = dragX !== null || (settle !== null && snapFromDrag)
  const dir = dragX === null ? dragDir : dragX < 0 ? 1 : -1
  const q = dragX !== null ? (width ? Math.min(Math.abs(dragX) / width, 1) : 0) : settle ? 1 : 0
  // A tapped snap names where the calendar is headed as it starts, not when it lands.
  const labelMonth = collapsing ? currentMonth : following ? month : shiftMonth(month, settle ?? 0)
  const titleFollow = following ? { to: shiftMonth(month, dir), q, dir, easing: dragX === null } : null
  const arrowsFollow = following && !expanded ? { opacity: q } : undefined
  const pageProps = { currentMonth, today, weekStartsOn, marks, crewHidden, settled, renderDayPlans: dayPlans, onDayToggle: dayToggled }

  return (
    <div className="touch-pan-y select-none rounded-2xl bg-card px-2 pb-3 pt-2" data-testid="plans-calendar" {...handlers}>
      {/* The whole bar toggles; the month arrows sit over it as siblings, since buttons can't nest. */}
      <div className="relative pb-1">
        <button type="button" onClick={toggle} aria-expanded={shown}
          aria-label={shown
            ? fbt('Show two weeks', 'Aria label for collapsing the plans calendar from a month to two weeks')
            : fbt('Show month', 'Aria label for expanding the plans calendar from two weeks to a month')}
          className="flex h-10 w-full cursor-pointer items-center rounded-lg pl-2 pr-2.5 text-left transition-colors hover:bg-muted/40 active:bg-muted/60">
          <PlansCalendarTitle month={labelMonth} format={formatMonth} follow={titleFollow} quiet={snapFromDrag && !collapsing} />
          <ChevronDown style={arrowsFollow && { rotate: `${q * 180}deg` }}
            className={cn('size-5 transition-transform duration-200', dragX !== null && 'transition-none',
              arrowsFollow && dragX === null && 'duration-300 ease-out', shown && 'rotate-180')} />
        </button>
        <div inert={!shown} style={arrowsFollow}
          className={cn('absolute inset-y-0 right-10 flex items-center transition-opacity duration-300 ease-out motion-reduce:transition-none',
            dragX !== null && 'transition-none', !shown && 'pointer-events-none opacity-0')}>
          <Button variant="ghost" size="icon" className="size-10" onClick={() => settleTo(-1)}
            aria-label={fbt('Previous month', 'Aria label for the plans calendar button showing the previous month')}>
            <ChevronLeft className="size-5" />
          </Button>
          <Button variant="ghost" size="icon" className="size-10" onClick={() => settleTo(1)}
            aria-label={fbt('Next month', 'Aria label for the plans calendar button showing the next month')}>
            <ChevronRight className="size-5" />
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-7">
        {Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)).map(d => (
          <span key={d} className="pb-1 text-center text-[11px] font-medium uppercase text-muted-foreground/70">
            {parseDateOnly(d).toLocaleDateString(bcp47, { weekday: 'narrow' })}
          </span>
        ))}
      </div>
      <div ref={measureViewport}
        style={{ height: `${frame.rows * WEEK_ROW_REM}rem`, transitionDuration: duration }}
        className={cn('overflow-hidden', easing)}>
        <div
          className={cn('relative', dragX === null && settle !== null && cn('transition-transform duration-300', curve))}
          style={{ transform: `translateX(calc(${-100 * (settle ?? 0)}% + ${offset}px))`, transitionDuration: duration }}
          onTransitionEnd={e => {
            if (e.target === e.currentTarget && e.propertyName === 'transform' && settle !== null) commit(settle)
          }}
        >
          {offsets.map(i => {
            const m = shiftMonth(month, i)
            return i === 0
              ? <MonthPage key={m} month={m} {...pageProps} expanded={expanded} openDay={openDay}
                  className={easing || undefined} style={shift} />
              : <MonthPage key={m} month={m} {...pageProps} expanded openDay={null} inert
                  className={cn('absolute top-0', easing)} style={{ ...shift, left: `${i * 100}%` }} />
          })}
        </div>
      </div>
      <PlansLegend crewHidden={crewHidden} />
    </div>
  )
}

function PlansLegend({ crewHidden }: { crewHidden?: boolean }) {
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 pt-2 text-[11px] text-muted-foreground">
      <span className="flex items-center gap-1.5">
        <GoingCheckIcon className="size-3.5" />
        {fbt('Going', 'Plans calendar legend: days with events the user is going to')}
      </span>
      <span className="flex items-center gap-1.5">
        <Heart className="size-3.5 fill-interested text-interested" />
        {fbt('Interested', 'Plans calendar legend: days with events the user is interested in')}
      </span>
      <span className={cn('flex items-center gap-1.5 transition-opacity', crewHidden && 'opacity-50')}>
        <span className={cn('h-1 w-2.5 rounded-full transition-colors', crewHidden ? 'bg-muted-foreground' : 'bg-friends')} />
        {fbt('Crew', "Plans calendar legend: days with friends' or groups' plans the user isn't part of")}
      </span>
    </div>
  )
}
